<template>
<div>
    <v-menu
        v-model="menu_open"
        :close-on-content-click="false"
        bottom
        offset-y
        max-height="600"
        min-width="350"
    >
        <template #activator="{ on, attrs }">
            <slot name="activator" :on="on" :attrs="attrs">
                <v-btn
                    v-if="prominent"
                    fab
                    dark
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-icon dark>mdi-dots-vertical</v-icon>
                </v-btn>
                
                <v-icon v-on="on" v-else>
                    mdi-dots-vertical
                </v-icon>
            </slot>
        </template>

        <v-card>
            <!-- Search field -->
            <v-card-text v-if="allow_search" class="pb-0">
                <v-text-field
                    v-model="search"
                    placeholder="Search actions..."
                    prepend-inner-icon="mdi-magnify"
                    dense
                    outlined
                    hide-details
                    clearable
                    @keydown.esc="menu_open = false"
                />
            </v-card-text>

            <v-list dense class="actions-menu-list">
                <!-- ADD RECIPIENTS section -->
                <v-subheader v-if="filtered_add_items.length > 0">
                    Add Recipients
                </v-subheader>
                <template v-for="(item, index) in filtered_add_items">
                    <!-- Deprecated item with tooltip -->
                    <v-tooltip
                        v-if="item.deprecated"
                        :key="'add-tooltip-' + index"
                        bottom
                        max-width="300"
                    >
                        <template #activator="{ on, attrs }">
                            <v-list-item
                                v-bind="attrs"
                                v-on="on"
                                @click="executeAction(item)"
                            >
                                <v-list-item-icon class="mr-3">
                                    <v-icon small color="grey">{{ item.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title class="grey--text text--darken-1">
                                        {{ item.text }}
                                        <v-icon small right color="grey">mdi-alert-circle-outline</v-icon>
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <span>This version is deprecated and scheduled for removal. Please use v2 above.</span>
                    </v-tooltip>
                    
                    <!-- Normal item -->
                    <v-list-item
                        v-else
                        :key="'add-' + index"
                        @click="executeAction(item)"
                    >
                        <v-list-item-icon class="mr-3">
                            <v-icon small :color="item.color || 'success'">{{ item.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ item.text }}
                                <m-menu-chip v-if="item.badge_new" :beta_feature_code="item.beta_feature_code" />
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>

                <v-divider v-if="filtered_add_items.length > 0 && filtered_remove_items.length > 0" class="my-1" />

                <!-- REMOVE RECIPIENTS section -->
                <v-subheader v-if="filtered_remove_items.length > 0">
                    Remove Recipients
                    <m-menu-chip :beta_feature_code="beta_feature_codes.bulk_message_remove_recipients" />
                </v-subheader>
                <v-list-item
                    v-for="(item, index) in filtered_remove_items"
                    :key="'remove-' + index"
                    @click="executeAction(item)"
                >
                    <v-list-item-icon class="mr-3">
                        <v-icon small :color="item.color || 'warning'">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ item.text }}
                            <m-menu-chip v-if="item.badge_new" :beta_feature_code="item.beta_feature_code" />
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <!-- IMPORTS & EXPORTS section -->
                <template v-if="filtered_import_export_items.length > 0">
                    <v-divider v-if="hasVisibleSectionBefore('import_export')" class="my-1" />

                    <v-subheader>Imports &amp; Exports</v-subheader>
                    <v-list-item
                        v-for="(item, index) in filtered_import_export_items"
                        :key="'import-export-' + index"
                        @click="executeAction(item)"
                    >
                        <v-list-item-icon class="mr-3"><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ item.text }}
                                <m-menu-chip v-if="item.badge_new" :beta_feature_code="item.beta_feature_code" />
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>

                <!-- VALIDATION section -->
                <template v-if="filtered_validation_items.length > 0">
                    <v-divider v-if="hasVisibleSectionBefore('validation')" class="my-1" />

                    <v-subheader>Validation</v-subheader>
                    <template v-for="(item, index) in filtered_validation_items">
                        <m-restricted-menu-list-item
                            v-if="item.restricted"
                            :key="'validation-restricted-' + index"
                            :text="item.text"
                            @click="executeAction(item)"
                        />
                        <v-list-item
                            v-else
                            :key="'validation-' + index"
                            @click="executeAction(item)"
                        >
                            <v-list-item-icon class="mr-3"><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </template>

                <!-- MESSAGE CONTROLS section -->
                <template v-if="filtered_message_control_items.length > 0">
                    <v-divider v-if="hasVisibleSectionBefore('message_controls')" class="my-1" />

                    <v-subheader>Message Controls</v-subheader>
                    <v-list-item
                        v-for="(item, index) in filtered_message_control_items"
                        :key="'message-control-' + index"
                        @click="executeAction(item)"
                    >
                        <v-list-item-icon class="mr-3"><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>

                <!-- Admin section (system admin only) -->
                <template v-if="filtered_admin_items.length > 0">
                    <v-divider v-if="hasVisibleSectionBefore('admin')" class="my-1" />

                    <v-subheader>Admin / Demo</v-subheader>
                    <m-restricted-menu-list-item
                        v-for="(item, index) in filtered_admin_items"
                        :key="'admin-' + index"
                        :text="item.text"
                        @click="executeAction(item)"
                    />
                </template>

                <!-- No results -->
                <v-list-item v-if="has_no_search_results">
                    <v-list-item-content>
                        <v-list-item-title class="text--disabled text-center">No matching actions found</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
    </v-menu>

    <!-- Dialogs -->
    <m-bulk-recipient-edit-dialog
        ref="edit-bulk-recipient"
        @ok="$emit('update-recipients')"
    />

    <m-autocomplete-phone-number
        ref="add-phone-numbers"
        action_button_text="Add Recipients"
        dialog_only
        default_multiselect
        @start-group-text="onAddMultiplePhoneNumbers"
    />

    <m-edit-select-dialog
        ref="select-list-add"
        multiple
        @ok="onAddListMembers"
    />

    <m-edit-select-dialog
        ref="select-list-remove"
        multiple
        @ok="onRemoveListMembers"
    />

    <m-model-r1-optimize-recipients-dialog
        ref="m-model-r1-optimize-recipients-dialog"
        @ok="$emit('update-recipients')"
    />

    <m-bulk-message-add-recipients-yet-to-customize-club-dialog
        ref="m-bulk-message-add-recipients-yet-to-customize-club-dialog"
        @ok="onOkAddRecipientsYetToCustomizeClubDialog"
    />

    <m-edit-blobs-dialog
        ref="import-wine-pulse-report-dialog"
        :externally_visible="false"
        :accept="['csv']"
        :allow_multiple="false"
        purpose="excel-import"
        title="Import WinePulse Report"
        winepulse
        @ok="onImportWinePulseReport"
    />

    <m-bulk-message-search-imports-dialog ref="bulk-message-search-imports-dialog" />
</div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { MESSAGE_STATES } from '@/js/bulk/bulk-states';

const beta_feature_codes = {
    bulk_message_import_v2: 'beta-bulk-message-import-v2',
    bulk_message_remove_recipients: 'beta-bulk-message-remove-recipients',
};

const ALL_MESSAGE_STATES = Object.values(MESSAGE_STATES);

export default {
    name: 'bulk-message-actions-menu',

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        message_state: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            default: null,
        },
        prominent: {
            type: Boolean,
            default: true,
        },
        allow_search: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            menu_open: false,
            search: '',
            beta_feature_codes: beta_feature_codes,
            wine_pulse_import_mode: 'add',
        };
    },

    watch: {
        menu_open(new_value) {
            if (new_value) {
                this.$emit('menu-opened');
            } else {
                setTimeout(() => {
                    this.search = '';
                }, 300); // let menu close animation finish
            }
        },
    },

    computed: {
        isSystemAdmin() {
            return this.$rc.$state.isAuthActorSystemAdmin();
        },

        isDemoOrganization() {
            return this.bulk_message && this.bulk_message.is_demo_organization;
        },

        add_items() {
            const items = [];

            items.push({ text: 'Add one recipient...', icon: 'mdi-account-plus-outline', action: this.onClickCreateRecipient });
            items.push({ text: 'Add multiple recipients (using contacts)...', icon: 'mdi-account-multiple', action: this.onClickAddContacts });
            items.push({ text: 'Add recipients from an Excel file (v2)...', icon: 'mdi-file-excel', badge_new: true, beta_feature_code: this.beta_feature_codes.bulk_message_import_v2, action: () => this.navigateToImport('add') });
            items.push({ text: 'Add recipients from Excel file (v1)...', icon: 'mdi-file-excel-outline', deprecated: true, action: this.onClickExcelImportLegacy });
            items.push({ text: 'Add recipients from a list...', icon: 'mdi-format-list-bulleted', action: this.onClickAddFromList });

            if (this.$rc.$state.isCRMEnabled('commerce7')) {
                items.push({ text: 'Add recipients using Commerce7 tag...', icon: 'mdi-tag', action: () => this.navigateToTag('commerce7') });
                items.push({ text: 'Add recipients that are Commerce7 active club members...', icon: 'mdi-account-group', action: () => this.navigateToClub('commerce7') });
                items.push({ text: 'Add recipients from Commerce7 who haven\'t customized their club shipment...', icon: 'mdi-package-variant',  action: this.onClickAddNotCustomClubShipment });
                items.push({ text: 'Add recipients from Commerce7 reservations...', icon: 'mdi-calendar-check', action: () => this.navigateToReservations('commerce7') });
            }

            if (this.$rc.$state.isCRMEnabled('tock-v2')) {
                items.push({ text: 'Add recipients from Tock reservations...', icon: 'mdi-calendar-check', action: () => this.navigateToReservations('tock-v2') });
            }

            if (this.$rc.$state.isCRMEnabled('ecellar')) {
                items.push({ text: 'Add recipients from eCellar reservations...', icon: 'mdi-calendar-check', action: () => this.navigateToReservations('ecellar') });
            }

            if (this.$rc.$state.isCRMEnabled('order-port')) {
                items.push({ text: 'Add recipients from OrderPort reservations...', icon: 'mdi-calendar-check', action: () => this.navigateToReservations('order-port') });
            }

            if (this.$rc.$state.isCRMEnabled('offset')) {
                items.push({ text: 'Add recipients from Offset groups...', icon: 'mdi-account-group', action: () => this.navigateToGroup('offset') });
            }

            if (this.$rc.$state.isCRMEnabled('vinoshipper')) {
                items.push({ text: 'Add recipients that are Vinoshipper club members...', icon: 'mdi-account-group', action: () => this.navigateToClub('vinoshipper') });
            }

            // Wine industry
            if (this.$rc.inIndustry('wine')) {
                items.push({ text: 'Add recipients from WinePulse report...', icon: 'mdi-file-chart', action: this.onClickStartImportWinePulse });
            }

            return items;
        },

        filtered_add_items() {
            return this.filterItemsByStateAndSearch(this.add_items);
        },

        remove_items() {
            const items = [];
            
            if (this.bulk_message && this.bulk_message.is_sentiment_forecast_available) {
                items.push({ text: 'RedChirp AI: optimize recipients...', icon: 'mdi-robot', action: () => this.onClickOptimizeRecipients(false) });
            }
            
            items.push({ text: 'Remove recipients using Excel file...', icon: 'mdi-file-excel-outline', action: () => this.navigateToImport('remove') });
            items.push({ text: 'Remove recipients based on a list...', icon: 'mdi-format-list-bulleted', action: this.onClickRemoveFromList });

            if (this.$rc.$state.isCRMEnabled('commerce7')) {
                items.push({ text: 'Remove recipients based on a Commerce7 tag...', icon: 'mdi-tag-off', action: () => this.navigateToTag('commerce7', 'remove') });
                items.push({ text: 'Remove recipients that are active Commerce7 club members...', icon: 'mdi-account-group-outline', action: () => this.navigateToClub('commerce7', 'remove') });
                items.push({ text: 'Remove recipients that had Commerce7 reservations...', icon: 'mdi-calendar-remove', action: () => this.navigateToReservations('commerce7', 'remove') });
                items.push({ text: 'Remove recipients from Commerce7 that placed a recent order...', icon: 'mdi-cart-remove', action: this.onClickRemoveRecipientsFromRecentOrders });

            }

            if (this.$rc.$state.isCRMEnabled('tock-v2')) {
                items.push({ text: 'Remove recipients based on Tock reservations...', icon: 'mdi-calendar-remove', action: () => this.navigateToReservations('tock-v2', 'remove') });
            }

            if (this.$rc.$state.isCRMEnabled('ecellar')) {
                items.push({ text: 'Remove recipients based on eCellar reservations...', icon: 'mdi-calendar-remove', action: () => this.navigateToReservations('ecellar', 'remove') });
            }

            if (this.$rc.$state.isCRMEnabled('order-port')) {
                items.push({ text: 'Remove recipients based on OrderPort reservations...', icon: 'mdi-calendar-remove', action: () => this.navigateToReservations('order-port', 'remove') });
            }

            if (this.$rc.$state.isCRMEnabled('offset')) {
                items.push({ text: 'Remove recipients based on Offset groups...', icon: 'mdi-account-group-outline', action: () => this.navigateToGroup('offset', 'remove') });
            }

            if (this.$rc.$state.isCRMEnabled('vinoshipper')) {
                items.push({ text: 'Remove recipients that are Vinoshipper club members...', icon: 'mdi-account-group-outline', action: () => this.navigateToClub('vinoshipper', 'remove') });
            }

            if (this.$rc.inIndustry('wine')) {
                items.push({ text: 'Remove recipients based on WinePulse report...', icon: 'mdi-file-chart-outline', action: this.onClickStartRemoveWinePulse });
            }
            
            //Remove all recipients
            items.push({ text: 'Delete all recipients...', icon: 'mdi-account-remove', action: this.deleteAllRecipients });

            return items;
        },

        filtered_remove_items() {
            return this.filterItemsByStateAndSearch(this.remove_items);
        },

        import_export_items() {
            return [
                { text: 'Export to Excel...', icon: 'mdi-file-export', available_in_message_states: ALL_MESSAGE_STATES, action: () => this.$emit('export-recipients-to-excel') },
                { text: 'View import history...', icon: 'mdi-history', badge_new: true, beta_feature_code: this.beta_feature_codes.bulk_message_import_v2, available_in_message_states: ALL_MESSAGE_STATES, action: this.onClickViewImports },
                { text: 'View all Excel files uploaded...', icon: 'mdi-folder-open', available_in_message_states: ALL_MESSAGE_STATES, action: this.onClickViewImportsLegacy },
            ];
        },

        filtered_import_export_items() {
            return this.filterItemsByStateAndSearch(this.import_export_items);
        },

        validation_items() {
            const items = [
                { text: 'View phone validation...', icon: 'mdi-phone-check', action: () => this.$emit('recipient-validation') },
                { text: 'Update recipient locations...', icon: 'mdi-map-marker', action: this.onClickUpdateRecipientLocations },
            ];
            if (this.isSystemAdmin) {
                items.push({ text: 'Update number reassignment check since dates...', icon: 'mdi-security', restricted: true, action: this.onClickUpdateNumberReassignmentCheckSinceDates });
            }
            return items;
        },

        filtered_validation_items() {
            return this.filterItemsByStateAndSearch(this.validation_items);
        },

        message_control_items() {
            return [
                { text: 'Split message...', icon: 'mdi-call-split', action: () => this.$emit('split-message') },
                { text: 'Adjust max error rate...', icon: 'mdi-alert-circle-outline', action: () => this.$emit('edit-error-rate-before-cancel') },
            ];
        },

        filtered_message_control_items() {
            return this.filterItemsByStateAndSearch(this.message_control_items);
        },

        admin_items() {
            if (!this.isSystemAdmin || !this.isDemoOrganization) return [];
            return [
                { text: 'Add dummy recipients...', action: this.onClickAddDummyRecipients },
                { text: 'Add dummy (spam) recipients...', action: this.onClickAddDummySpamRecipients },
                { text: 'Export dummy list of recipients...', action: this.onClickDummyExport },
                { text: 'Export dummy WinePulse report...', action: this.onClickWinePulseDummy },
            ];
        },

        filtered_admin_items() {
            return this.filterItemsByStateAndSearch(this.admin_items);
        },

        has_no_search_results() {
            if (!this.search) return false;
            return Object.values(this.visible_sections).every(visible => !visible);
        },

        visible_sections() {
            return {
                add: this.filtered_add_items.length > 0,
                remove: this.filtered_remove_items.length > 0,
                import_export: this.filtered_import_export_items.length > 0,
                validation: this.filtered_validation_items.length > 0,
                message_controls: this.filtered_message_control_items.length > 0,
                admin: this.filtered_admin_items.length > 0,
            };
        },

        section_order() {
            return ['add', 'remove', 'import_export', 'validation', 'message_controls', 'admin'];
        },
    },

    methods: {
        filterItemsByStateAndSearch(items) {
            const state_filtered = items.filter(item => {
                // most actions are only available in EDIT state unless otherwise specified
                const available_states = item.available_in_message_states || [MESSAGE_STATES.EDIT];
                return available_states.includes(this.message_state);
            });
            if (!this.search) return state_filtered;
            const s = this.search.toLowerCase();
            return state_filtered.filter(i => i.text.toLowerCase().includes(s) || (i.subtitle && i.subtitle.toLowerCase().includes(s)));
        },

        hasVisibleSectionBefore(section_key) {
            const index = this.section_order.indexOf(section_key);
            for (let i = 0; i < index; i++) {
                if (this.visible_sections[this.section_order[i]]) return true;
            }
            return false;
        },
        
        openMenu() {
            this.menu_open = true;
        },

        executeAction(item) {
            this.menu_open = false;
            this.search = '';
            if (item.action) item.action();
        },

        onClickCreateRecipient() {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.organization_id,
                this.bulk_message_id,
                null
            );
        },

        onClickAddContacts() {
            this.$refs['add-phone-numbers'].showDialog();
        },

        onAddMultiplePhoneNumbers(items) {
            const endpoints = [];

            for (let i = 0; i < items.length; i++) {
                endpoints.push(items[i].value);
            }

            const params = {
                bulk_message_id: this.bulk_message_id,
                endpoints
            };

            this.$rc.showSpinner();

            this.$rc.rest(
                'POST: /api/v1/bulk/recipients/put-contacts',
                params,

                (data) => {
                    this.$rc.hideSpinner();
                    this.$emit('update-recipients');
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to add recipients');
                },
            );
        },

        onClickAddFromList() {
            this.$rc.showSpinner();
            this.$rc.rest(
                'LIST: /api/v1/lists/choose-org-list',
                { organization_id: this.organization_id, include_auto_lists: true },
                (data) => {
                    this.$rc.hideSpinner();
                    this.$refs['select-list-add'].showDialog('Select List', null, data.results);
                },
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to fetch lists');
                },
            );
        },

        onAddListMembers(list_id) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/recipients/list-to-preview',
                params: {
                    list_id,
                    bulk_message_id: this.bulk_message_id,
                    rest_timeout: 60000,
                },
                on_success: (data) => {
                    this.$router.push({
                        name: 'bulk-message-recipients-import-upload',
                        params: { organization_id: data.organization_id, import_id: data.import_id },
                        query: { bulk_message_id: data.bulk_message_id, return_to: 'view-bulk-message' },
                    });
                },
                on_error: 'Unable to add list members to recipients',
            });
        },

        onClickRemoveFromList() {
            this.$rc.showSpinner();
            this.$rc.rest(
                'LIST: /api/v1/lists/choose-org-list',
                { organization_id: this.organization_id, include_auto_lists: true },
                (data) => {
                    this.$rc.hideSpinner();
                    this.$refs['select-list-remove'].showDialog('Select List to Remove Recipients From', null, data.results);
                },
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to fetch lists');
                },
            );
        },

        onRemoveListMembers(list_id) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/recipients/list-to-preview',
                params: {
                    list_id,
                    bulk_message_id: this.bulk_message_id,
                    rest_timeout: 60000,
                    import_execution_mode: 'remove',
                },
                on_success: (data) => {
                    this.$router.push({
                        name: 'bulk-message-recipients-import-upload',
                        params: { organization_id: data.organization_id, import_id: data.import_id },
                        query: { bulk_message_id: data.bulk_message_id, return_to: 'view-bulk-message', mode: 'remove' },
                    });
                },
                on_error: 'Unable to create removal preview from list members',
            });
        },

        onClickViewImportsLegacy() {
            this.$refs['bulk-message-search-imports-dialog'].showDialog();
        },

        onClickViewImports() {
            this.$router.push({
                name: 'search-bulk-message-recipient-imports',
                params: {
                    organization_id: this.organization_id,
                },
                query: {
                    bulk_message_id: this.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onClickUpdateNumberReassignmentCheckSinceDates() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/compute-missing-number-reassignment-check-since-dates',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    recompute_all: true,
                },

                on_success: (data) => {
                    this.$rc.alert('Updating number reassignment check since dates', 'The process of updating the number reassignment check since dates for all recipients has been started.  This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                },

                on_error: 'Unable to compute number reassignment check since dates',
            });
        },

        onClickUpdateRecipientLocations() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/compute-missing-location',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    recompute_all: true,
                },

                on_success: (data) => {
                    this.$rc.alert('Updating Recipient Location', 'The process of updating the location for all recipients has been started. This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                },

                on_error: 'Unable to compute locations',
            });
        },

        deleteAllRecipients() {
            this.$rc.confirm(
                'Delete All Recipients',
                'Are you sure you want to delete all recipients? This action cannot be undone.',
                [
                    { text: 'Cancel', value: 'cancel' },
                    { text: 'Confirm', value: 'confirm' },
                ],
                (response) => {
                    if (response === 'confirm') {
                        this.$rc.rest({
                            spinner: false,
                            endpoint: 'DELETE: /api/v1/bulk/recipients/all',

                            params: {
                                bulk_message_id: this.bulk_message_id
                            },

                            on_success: (data) => {
                                this.$emit('update-recipients');
                            },
                            on_error: 'There was an error deleting the recipients, please try again.',
                        });
                    }
                    else {
                        return;
                    }
                }
            );
        },

        onClickExcelImportLegacy() {
            const import_id = uuidv4();

            this.$router.push({
                name: 'bulk-recipient-import',
                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                    import_id,
                }
            }).catch(e => { }); // swallow any exception
        },
        
        onClickRemoveRecipientsFromRecentOrders() {
            this.$router.push({
                name: 'bulk-recipient-remove-recent-order',
                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        promptForDummyRecipientCount(after_action) {
            this.$rc.prompt({
                title: 'Number of recipients',
                label: 'Number of recipients',
                value: '300',
                after_prompt: (value) => {
                    const size = parseInt(value);
                    if (Number.isNaN(size)) return;
                    after_action(size);
                }
            });
        },

        onClickAddDummyRecipients() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'PUT: /api/v1/bulk/add-dummy-recipients',

                        params: {
                            size,
                            bulk_message_id: this.bulk_message_id,
                        },

                        on_success: (data) => {
                            this.$emit('update');
                        },

                        on_error: 'Unable to add dummy recipients',
                    });
                }
            );
        },

        onClickAddDummySpamRecipients() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'PUT: /api/v1/bulk/add-dummy-recipients',

                        params: {
                            size,
                            bulk_message_id: this.bulk_message_id,
                            number_type: 'spam'
                        },

                        on_success: (data) => {
                            this.$emit('update');
                        },
                        on_error: 'Unable to add dummy recipients',
                    });
                }
            );
        },

        onClickDummyExport() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.excelExport(
                        'EXPORT: /api/v1/bulk/dummy-recipients',
                        { size }
                    );
                }
            );
        },

        onClickWinePulseDummy() {
            window.open('https://files.redchirp.com/ecwqreeducufuzsybbbcxtedvfyuwvse.csv', '_blank');
        },

        onClickOptimizeRecipients(read_only) {
            this.$refs['m-model-r1-optimize-recipients-dialog'].showDialog(this.bulk_message.recipient_count, read_only);
        },

        onClickAddNotCustomClubShipment() {
            this.$refs['m-bulk-message-add-recipients-yet-to-customize-club-dialog'].showDialog();
        },

        onOkAddRecipientsYetToCustomizeClubDialog(club_release) {
            /* this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/add-dummy-recipients',
    
                'params' : {
                    'size': 723,
                    'bulk_message_id': this.bulk_message_id,
                },
    
                'on_success': (data) => {
                    this.$emit('update');
                },
    
                'on_error': 'Unable to add club members that have not customized this shipment',
            }); */

            this.$rc.startProcess({
                type: 'crm-create-excel-preview-of-customers-who-have-not-customized-club-release',

                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                    ...club_release
                },

                title: 'Listing customers who have not customized club release',
                finished_button_text: 'Preview Recipients',
                failed_button_text: 'Cancel',

                status: (pte) => {
                    return 'Customer phone numbers: ' + pte.progress_counter;
                },

                on_finished: (pte) => {
                    this.$router.push({
                        name: 'bulk-message-recipients-import-upload',
                        params: {
                            organization_id: pte.output.organization_id,
                            import_id: pte.output.import_id
                        },
                        query: {
                            bulk_message_id: pte.output.bulk_message_id,
                            return_to: 'view-bulk-message',
                        }
                    });
                },

                on_failed: (pte) => {
                },
            });
        },

        onClickStartImportWinePulse() {
            this.wine_pulse_import_mode = 'add';
            this.$refs['import-wine-pulse-report-dialog'].showDialog(
                this.organization_id,
                this.bulk_message_id
            );
        },

        onClickStartRemoveWinePulse() {
            this.wine_pulse_import_mode = 'remove';
            this.$refs['import-wine-pulse-report-dialog'].showDialog(
                this.organization_id,
                this.bulk_message_id
            );
        },

        onImportWinePulseReport(blobs) {
            if (blobs.length == 0) return;

            const blob_id = blobs[0];

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/recipients/wine-pulse-import',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    blob_id,
                    import_execution_mode: this.wine_pulse_import_mode === 'remove' ? 'remove' : 'add_or_update',
                },

                on_success: (data) => {
                    const query = {
                        bulk_message_id: this.bulk_message_id,
                        return_to: 'view-bulk-message',
                    };
                    if (this.wine_pulse_import_mode === 'remove') {
                        query.mode = 'remove';
                    }

                    this.$router.push({
                        name: 'bulk-message-recipients-import-upload',
                        params: {
                            organization_id: data.organization_id,
                            import_id: data.import_id
                        },
                        query
                    });
                },

                on_error: 'Unable to import WinePulse report',
            });
        },

        navigateToImport(mode) {
            const query = { bulk_message_id: this.bulk_message_id, return_to: 'view-bulk-message' };
            if (mode === 'remove') query.mode = 'remove';
            this.$router.push({ name: 'bulk-message-recipients-import-upload', params: { organization_id: this.organization_id }, query });
        },

        navigateToReservations(contentType, mode) {
            const query = { add_to_bulk_message_id: this.bulk_message_id };
            if (mode === 'remove') query.mode = 'remove';
            this.$router.push({ name: 'crm-reservations', params: { organization_id: this.organization_id, content_type: contentType }, query });
        },

        navigateToTag(integrationType, mode) {
            const query = { add_to_bulk_message_id: this.bulk_message_id };
            if (mode === 'remove') query.mode = 'remove';
            this.$router.push({ name: 'crm-tag-meta-data', params: { organization_id: this.organization_id, content_type: integrationType }, query });
        },

        navigateToClub(integrationType, mode) {
            const query = { add_to_bulk_message_id: this.bulk_message_id };
            if (mode === 'remove') query.mode = 'remove';
            this.$router.push({ name: 'crm-club-meta-data', params: { organization_id: this.organization_id, content_type: integrationType }, query });
        },

        navigateToGroup(integrationType, mode) {
            const query = { add_to_bulk_message_id: this.bulk_message_id };
            if (mode === 'remove') query.mode = 'remove';
            this.$router.push({ name: 'crm-group-meta-data', params: { organization_id: this.organization_id, integration_type: integrationType }, query });
        },
    },
};
</script>

<style scoped>
.actions-menu-list {
    max-height: 500px;
    overflow-y: auto;
}
</style>
